import type { ReactNode } from "react";
import { PHASES, ROLE_LABELS } from "../lib/labels";
import { formatPeriod } from "../lib/utils";

interface Project {
  period: {
    start: string;
    end: string | null;
  };
  title: string;
  technologies: {
    languages: string[];
    database: string[];
    os: string[];
    frameworks: string[];
  };
  phases: {
    requirement_definition: boolean;
    basic_design: boolean;
    detailed_design: boolean;
    implementation: boolean;
    integration_test: boolean;
    system_test: boolean;
    maintenance: boolean;
  };
  responsibilities: {
    tasks: string[];
    comment: string;
  };
  challenges: string;
  actions: string;
  achievements: string;
  team: {
    roles: Record<string, number>;
  };
}

interface ProjectCardProps {
  project: Project;
}

export function ProjectCard({ project }: ProjectCardProps) {
  // チーム総数を計算する関数（各役職の人数を足し算）
  const calculateTeamTotal = (roles: Record<string, number>): number => {
    return Object.values(roles).reduce((total, count) => total + count, 0);
  };

  const technologies = [
    ...project.technologies.languages,
    ...project.technologies.frameworks,
    ...project.technologies.database,
    ...project.technologies.os,
  ];

  const teamBreakdown = Object.entries(project.team.roles)
    .map(([role, count]) => `${ROLE_LABELS[role] ?? role} ${count}人`)
    .join("、");

  return (
    <section className="no-page-break border-t border-rule py-8 print:py-4 first:border-t-0 first:pt-0 last:pb-0">
      <p className="text-[13px] font-medium tabular-nums text-accent">
        {formatPeriod(project.period)}
      </p>
      <h4 className="jp-heading mt-1 text-lg print:text-[15px] font-bold leading-snug text-ink">
        {project.title}
      </h4>

      <ul className="mt-3 print:mt-2 flex flex-wrap gap-1.5 print:gap-1">
        {technologies.map((tech, index) => (
          <li
            key={index}
            className="rounded-[3px] bg-tint px-2 py-0.5 print:py-0 text-[12.5px] print:text-[11px] text-ink-2"
          >
            {tech}
          </li>
        ))}
      </ul>

      <dl className="mt-6 print:mt-3 flex flex-col gap-4 print:gap-1.5 text-[14.5px] leading-[27px] print:text-[13.5px] print:leading-[23px] text-ink">
        {PHASES.some(([key]) => project.phases[key]) && (
          <Item label="フェーズ">
            <ul className="flex flex-wrap gap-x-4">
              {PHASES.map(([key, label]) => {
                const included = project.phases[key];
                return (
                  <li
                    key={key}
                    className={`flex items-center gap-1.5 ${included ? "text-ink" : "text-ink-3"}`}
                  >
                    <span
                      aria-hidden
                      className={`size-2 ${included ? "bg-accent" : "border border-ink-3/60"}`}
                    />
                    {label}
                    {!included && <span className="sr-only">（担当外）</span>}
                  </li>
                );
              })}
            </ul>
          </Item>
        )}

        <Item label="担当業務">
          <ul className="list-disc pl-5 marker:text-ink-3 print:columns-2 print:gap-x-8">
            {project.responsibilities.tasks.map((task, index) => (
              <li key={index}>{task}</li>
            ))}
          </ul>
        </Item>

        {project.responsibilities.comment && (
          <Item label="詳細">{project.responsibilities.comment}</Item>
        )}
        <Item label="課題">{project.challenges}</Item>
        <Item label="解決アクション">{project.actions}</Item>
        <Item label="実績">{project.achievements}</Item>
        <Item label="チーム">
          {calculateTeamTotal(project.team.roles)}人（{teamBreakdown}）
        </Item>
      </dl>
    </section>
  );
}

function Item({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-x-5 gap-y-0.5 sm:grid-cols-[6.5rem_1fr] print:grid-cols-[5.5rem_1fr] print:gap-x-3">
      <dt className="text-[13px] print:text-[11.5px] font-bold text-ink-2">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
